import {
  ALL_FORMATS,
  AudioSample,
  AudioSampleSink,
  AudioSampleSource,
  BlobSource,
  CanvasSource,
  Input,
  Mp4OutputFormat,
  Output,
  Quality,
  StreamTarget,
  type StreamTargetChunk,
} from "mediabunny";
import type { EditingProject } from "../types/project";
import { framesToSeconds } from "../core/time";
import { applyBoundaryRamp } from "./clickSuppression";
import { createOpfsOutput, getOpfsFile, removeOpfsFile } from "../storage/opfs";
import { exportCapabilityError, inspectCapabilities } from "./capabilities";
import { patchAacEditList } from "./gaplessMp4";

export type ExportProgress = {
  progress: number;
  elapsedSeconds: number;
  etaSeconds: number | null;
  phase: "準備中" | "音声を処理中" | "MP4を仕上げ中";
};

export type ExportResult = {
  file: File;
  opfsName: string;
  durationFrames: number;
  elapsedSeconds: number;
  mimeType: string;
  audioFramesSubmitted: number;
  videoFramesSubmitted: number;
  aacMode: "native" | "wasm" | "unavailable";
  encoderDelayFrames: number;
};

export type ExportOptions = {
  title: string;
  suppressClicks: boolean;
  signal: AbortSignal;
  onProgress: (progress: ExportProgress) => void;
};

export async function exportProjectToMp4(
  file: File,
  project: EditingProject,
  options: ExportOptions,
): Promise<ExportResult> {
  const startedAt = performance.now();
  const durationFrames = project.endFrame - project.startFrame;
  if (durationFrames <= 0)
    throw new Error("STARTとENDの間隔を1サンプル以上にしてください");
  const capabilities = await inspectCapabilities(
    project.sampleRate,
    project.channels,
  );
  const capabilityError = exportCapabilityError(capabilities);
  if (capabilityError) throw new Error(capabilityError);
  assertNotAborted(options.signal);

  const safeBase = safeFilename(project.fileName.replace(/\.[^.]+$/, ""));
  const opfsName = `${crypto.randomUUID()}-${safeBase}.mp4`;
  const downloadName = buildOutputName(project);
  const { handle, writable } = await createOpfsOutput(opfsName);
  const input = new Input({
    formats: ALL_FORMATS,
    source: new BlobSource(file, { maxCacheSize: 4 * 1024 * 1024 }),
  });
  let output: Output | null = null;
  let wakeLock: WakeLockSentinel | null = null;
  try {
    wakeLock =
      (await navigator.wakeLock?.request("screen").catch(() => null)) ?? null;
    options.onProgress({
      progress: 0,
      elapsedSeconds: 0,
      etaSeconds: null,
      phase: "準備中",
    });
    const track = await input.getPrimaryAudioTrack();
    if (!track) throw new Error("入力音声トラックが見つかりません");
    if (!(await track.canDecode()))
      throw new Error(
        `入力コーデック ${project.codec} をこの端末でデコードできません`,
      );

    const canvas = renderTitleCanvas(options.title);
    const videoSource = new CanvasSource(canvas, {
      codec: "avc",
      quality: new Quality({ bitrate: 250_000 }),
      fullCodecString: "avc1.42001f",
      hardwareAcceleration: "no-preference",
      latencyMode: "quality",
    });
    const audioSource = new AudioSampleSource({
      codec: "aac",
      quality: new Quality({ bitrate: 192_000 }),
      bitrateMode: "variable",
    });
    output = new Output({
      format: new Mp4OutputFormat({ fastStart: "reserve" }),
      target: new StreamTarget(
        writable as unknown as WritableStream<StreamTargetChunk>,
        {
          chunked: true,
          chunkSize: 1024 * 1024,
        },
      ),
    });
    output.addVideoTrack(videoSource, {
      name: options.title || "Black background",
      languageCode: "und",
      maximumPacketCount: 2,
    });
    output.addAudioTrack(audioSource, {
      name: project.fileName,
      languageCode: "und",
      maximumPacketCount: estimateMaximumAacPackets(
        durationFrames,
        project.sampleRate,
      ),
    });
    await output.start();

    const calibrationFrames = Math.max(
      4096,
      Math.ceil(project.sampleRate * 0.125),
    );
    const calibrationImpulseFrame = Math.ceil(project.sampleRate * 0.025);

    const durationSeconds = framesToSeconds(durationFrames, project.sampleRate);
    const lastFrameDuration = Math.min(1 / 30, durationSeconds);
    if (durationSeconds <= 1 / 30) {
      await videoSource.add(0, durationSeconds, { keyFrame: true });
    } else {
      await videoSource.add(0, durationSeconds - lastFrameDuration, {
        keyFrame: true,
      });
      await videoSource.add(
        durationSeconds - lastFrameDuration,
        lastFrameDuration,
        { keyFrame: true },
      );
    }

    const calibration = new Float32Array(calibrationFrames * project.channels);
    for (let channel = 0; channel < project.channels; channel += 1) {
      calibration[channel * calibrationFrames + calibrationImpulseFrame] = 0.9;
    }
    const calibrationSample = new AudioSample({
      data: calibration,
      format: "f32-planar",
      numberOfChannels: project.channels,
      sampleRate: project.sampleRate,
      timestamp: 0,
    });
    await audioSource.add(calibrationSample);
    calibrationSample.close();

    const sink = new AudioSampleSink(track);
    const sourceStart =
      project.trackStartSeconds + project.startFrame / project.sampleRate;
    const sourceEnd =
      project.trackStartSeconds + project.endFrame / project.sampleRate;
    const rampFrames = options.suppressClicks
      ? Math.max(1, Math.round(project.sampleRate * 0.002))
      : 0;
    let submittedFrames = 0;
    let lastProgressAt = 0;

    for await (const sample of sink.samples(sourceStart, sourceEnd)) {
      assertNotAborted(options.signal);
      const sourceSampleStart = Math.round(
        (sample.timestamp - project.trackStartSeconds) * project.sampleRate,
      );
      const sourceSampleEnd = sourceSampleStart + sample.numberOfFrames;
      const intersectionStart = Math.max(project.startFrame, sourceSampleStart);
      const intersectionEnd = Math.min(project.endFrame, sourceSampleEnd);
      const frameCount = intersectionEnd - intersectionStart;
      if (frameCount <= 0) {
        sample.close();
        continue;
      }
      const frameOffset = intersectionStart - sourceSampleStart;
      const planar = new Float32Array(frameCount * project.channels);
      for (let channel = 0; channel < project.channels; channel += 1) {
        const plane = planar.subarray(
          channel * frameCount,
          (channel + 1) * frameCount,
        );
        sample.copyTo(plane, {
          planeIndex: channel,
          format: "f32-planar",
          frameOffset,
          frameCount,
        });
      }
      sample.close();
      applyBoundaryRamp(
        planar,
        project.channels,
        frameCount,
        submittedFrames,
        durationFrames,
        rampFrames,
      );
      const outputSample = new AudioSample({
        data: planar,
        format: "f32-planar",
        numberOfChannels: project.channels,
        sampleRate: project.sampleRate,
        timestamp: (calibrationFrames + submittedFrames) / project.sampleRate,
      });
      await audioSource.add(outputSample);
      outputSample.close();
      submittedFrames += frameCount;

      const now = performance.now();
      if (now - lastProgressAt >= 120 || submittedFrames === durationFrames) {
        const progress = Math.min(0.98, submittedFrames / durationFrames);
        const elapsedSeconds = (now - startedAt) / 1000;
        const etaSeconds =
          progress > 0 ? (elapsedSeconds / progress) * (1 - progress) : null;
        options.onProgress({
          progress,
          elapsedSeconds,
          etaSeconds,
          phase: "音声を処理中",
        });
        lastProgressAt = now;
      }
    }
    if (submittedFrames !== durationFrames) {
      throw new Error(
        `抽出フレーム数が一致しません（要求 ${durationFrames} / 実際 ${submittedFrames}）`,
      );
    }

    options.onProgress({
      progress: 0.99,
      elapsedSeconds: (performance.now() - startedAt) / 1000,
      etaSeconds: null,
      phase: "MP4を仕上げ中",
    });
    assertNotAborted(options.signal);
    await output.finalize();
    output = null;
    const gapless = await patchAacEditList(
      handle,
      durationFrames,
      project.sampleRate,
      calibrationFrames,
      calibrationImpulseFrame,
    );
    const stored = await handle.getFile();
    const resultFile = new File([stored], downloadName, {
      type: "video/mp4",
      lastModified: Date.now(),
    });
    const elapsedSeconds = (performance.now() - startedAt) / 1000;
    options.onProgress({
      progress: 1,
      elapsedSeconds,
      etaSeconds: 0,
      phase: "MP4を仕上げ中",
    });
    return {
      file: resultFile,
      opfsName,
      durationFrames,
      elapsedSeconds,
      mimeType: "video/mp4",
      audioFramesSubmitted: submittedFrames,
      videoFramesSubmitted: durationSeconds <= 1 / 30 ? 1 : 2,
      aacMode: capabilities.aacMode,
      encoderDelayFrames: gapless.encoderDelayFrames,
    };
  } catch (error) {
    if (output && output.state !== "canceled" && output.state !== "finalized")
      await output.cancel().catch(() => undefined);
    await removeOpfsFile(opfsName);
    if (isAbortError(error))
      throw new DOMException("動画生成をキャンセルしました", "AbortError");
    throw error;
  } finally {
    input.dispose();
    await wakeLock?.release().catch(() => undefined);
  }
}

export async function reopenExport(
  opfsName: string,
  downloadName: string,
): Promise<File> {
  const stored = await getOpfsFile(opfsName);
  return new File([stored], downloadName, {
    type: "video/mp4",
    lastModified: stored.lastModified,
  });
}

export function estimateOutputBytes(
  durationFrames: number,
  sampleRate: number,
): number {
  const seconds = durationFrames / sampleRate;
  return Math.ceil((seconds * (192_000 + 4_000)) / 8 + 256 * 1024);
}

export function estimateMaximumAacPackets(
  durationFrames: number,
  sampleRate: number,
): number {
  const conservativePacketFrames = Math.min(
    512,
    Math.max(1, Math.floor(sampleRate * 0.01)),
  );
  return Math.ceil((durationFrames / conservativePacketFrames) * 1.5) + 16;
}

export function buildOutputName(project: EditingProject): string {
  const base = safeFilename(project.fileName.replace(/\.[^.]+$/, ""));
  const start = Math.floor(project.startFrame / project.sampleRate);
  const end = Math.floor(project.endFrame / project.sampleRate);
  return `${base}_${clockFilename(start)}-${clockFilename(end)}.mp4`;
}

function renderTitleCanvas(title: string): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = 1280;
  canvas.height = 720;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("動画描画用Canvasを作成できません");
  context.fillStyle = "#000";
  context.fillRect(0, 0, canvas.width, canvas.height);
  const normalized = title.trim().replace(/\s+/g, " ");
  if (!normalized) return canvas;
  context.fillStyle = "#fff";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = "600 64px -apple-system, BlinkMacSystemFont, sans-serif";
  const lines = splitTitle(context, normalized, 1040);
  const fontSize = lines.some((line) => context.measureText(line).width > 1040)
    ? 48
    : 64;
  context.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
  const lineHeight = fontSize * 1.3;
  lines.slice(0, 2).forEach((line, index) => {
    const y = canvas.height / 2 + (index - (lines.length - 1) / 2) * lineHeight;
    context.fillText(line, canvas.width / 2, y, 1040);
  });
  return canvas;
}

function splitTitle(
  context: CanvasRenderingContext2D,
  title: string,
  maxWidth: number,
): string[] {
  if (context.measureText(title).width <= maxWidth) return [title];
  const characters = [...title];
  let split = Math.ceil(characters.length / 2);
  let best = [
    characters.slice(0, split).join(""),
    characters.slice(split).join(""),
  ];
  let bestWidth = Math.max(
    ...best.map((line) => context.measureText(line).width),
  );
  for (
    let candidate = Math.max(1, split - 12);
    candidate <= Math.min(characters.length - 1, split + 12);
    candidate += 1
  ) {
    const lines = [
      characters.slice(0, candidate).join("").trim(),
      characters.slice(candidate).join("").trim(),
    ];
    const width = Math.max(
      ...lines.map((line) => context.measureText(line).width),
    );
    if (width < bestWidth) {
      best = lines;
      bestWidth = width;
      split = candidate;
    }
  }
  return best;
}

function assertNotAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException("Aborted", "AbortError");
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function safeFilename(value: string): string {
  const printable = [...value]
    .map((character) => (character.charCodeAt(0) < 32 ? "_" : character))
    .join("");
  return (
    printable
      .replace(/[\\/:*?"<>|]/g, "_")
      .replace(/\.+$/g, "")
      .trim()
      .slice(0, 80) || "VoiceCutter"
  );
}

function clockFilename(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join("-");
}
